import {
  isNotDefined,
  isNotEmpty,
  type CompanyData,
  type CompanyId,
  type ReportKpiFilter,
  type TeamData,
  type TeamId,
  type UserId,
} from '@terros/common'
import { getLevelForChildTeam } from '../data'

export type QuestionReportScopeSelection =
  | { teamId?: undefined; managerId?: undefined }
  | { teamId: TeamId; managerId?: undefined }
  | { teamId?: undefined; managerId: UserId }

export type QuestionReportScope = { companyId: CompanyId } & QuestionReportScopeSelection

export const isQuestionReportScoped = (scope: QuestionReportScope): boolean => Boolean(scope.teamId || scope.managerId)

export const selectQuestionReportScope = (
  scope: QuestionReportScope,
  selection: QuestionReportScopeSelection
): QuestionReportScope => {
  if (selection.managerId) return { companyId: scope.companyId, managerId: selection.managerId }
  if (selection.teamId) return { companyId: scope.companyId, teamId: selection.teamId }
  return { companyId: scope.companyId }
}

export const getScopeFilter = (scope: QuestionReportScope): ReportKpiFilter => {
  if (scope.managerId) return { companyId: scope.companyId, managers: [scope.managerId] }
  if (!scope.teamId) return { companyId: scope.companyId }
  return { companyId: scope.companyId, teams: [scope.teamId] }
}

export const getChildTeams = (
  scope: QuestionReportScope,
  teams: TeamData[] | undefined,
  company: Pick<CompanyData, 'highestTeamLevel'> | undefined
): TeamData[] => {
  if (!teams) return []
  if (scope.managerId) return []
  if (!scope.teamId) {
    const highestTeamLevel = company?.highestTeamLevel
    if (isNotDefined(highestTeamLevel)) return []
    return teams.filter((team) => team.level === highestTeamLevel)
  }
  return teams.filter((team) => team.parentId === scope.teamId)
}

export const getChildTeamLevel = (
  scope: QuestionReportScope,
  childTeams: TeamData[],
  teams: TeamData[]
): number | undefined => {
  if (scope.managerId) return
  if (isNotEmpty(childTeams)) return childTeams[0].level
  const selectedTeam = getSelectedTeam(scope, teams)
  if (selectedTeam) return getLevelForChildTeam(selectedTeam.level)
}

const getSelectedTeam = (scope: QuestionReportScope, teams: TeamData[] | undefined): TeamData | undefined => {
  if (scope.managerId) return
  if (!scope.teamId) return undefined
  return teams?.find((team) => team.teamId === scope.teamId)
}

const getTeamPath = (team: TeamData, teams: TeamData[]): TeamData[] => {
  const path: TeamData[] = []
  const visitedTeamIds = new Set<TeamId>()
  let currentTeam: TeamData | undefined = team

  while (currentTeam && !visitedTeamIds.has(currentTeam.teamId)) {
    path.unshift(currentTeam)
    visitedTeamIds.add(currentTeam.teamId)
    currentTeam = teams.find((candidate) => candidate.teamId === currentTeam?.parentId)
  }

  return path
}

export const getScopeLabel = (
  scope: QuestionReportScope,
  teams: TeamData[],
  managerName?: string
): string | undefined => {
  if (scope.managerId) return managerName ?? scope.managerId
  const selectedTeam = getSelectedTeam(scope, teams)
  if (!selectedTeam) return
  return getTeamPath(selectedTeam, teams)
    .map((team) => team.name)
    .join(' / ')
}

export const getScopeTeamPath = (scope: QuestionReportScope, teams: TeamData[]): TeamData[] => {
  if (scope.managerId) return []
  const selectedTeam = getSelectedTeam(scope, teams)
  if (!selectedTeam) return []
  return getTeamPath(selectedTeam, teams)
}

export const getScopeSiblingTeams = (scope: QuestionReportScope, teams: TeamData[]): TeamData[] => {
  if (scope.managerId) return []
  const selectedTeam = getSelectedTeam(scope, teams)
  if (!selectedTeam) return []
  return teams.filter((team) => team.parentId === selectedTeam.parentId && team.level === selectedTeam.level)
}

export type ScopeBreadcrumbs = {
  teams: TeamData[]
  hasOverflow: boolean
}

export const toScopeBreadcrumbs = (path: TeamData[], maxCrumbs = 3): ScopeBreadcrumbs => {
  if (path.length <= maxCrumbs) return { teams: path, hasOverflow: false }
  return { teams: path.slice(path.length - maxCrumbs), hasOverflow: true }
}
